import React from 'react';
import { ExternalLink, Check, AlertTriangle, X, ArrowRight, RefreshCw } from 'lucide-react';

/**
 * Компонент для визуализации цепочки редиректов
 */
const RedirectMap = ({ redirectChain }) => {
  if (!redirectChain || redirectChain.length === 0) {
    return null;
  }

  // Получение иконки для HTTP-статуса
  const getStatusIcon = (status) => {
    if (status === 0) {
      return <X className="w-5 h-5 text-red-500" />;
    } else if (status >= 200 && status < 300) {
      return <Check className="w-5 h-5 text-green-500" />;
    } else if (status >= 300 && status < 400) {
      return <RefreshCw className="w-5 h-5 text-blue-500" />;
    } else if (status >= 400 && status < 500) {
      return <AlertTriangle className="w-5 h-5 text-orange-500" />;
    } else if (status >= 500) {
      return <X className="w-5 h-5 text-red-500" />;
    }
    return null;
  };

  // Получение цвета для HTTP-статуса
  const getStatusColor = (status) => {
    if (status === 0) return 'border-red-500 bg-red-50 dark:bg-red-900/10';
    if (status >= 200 && status < 300) return 'border-green-500 bg-green-50 dark:bg-green-900/10';
    if (status >= 300 && status < 400) return 'border-blue-500 bg-blue-50 dark:bg-blue-900/10';
    if (status >= 400 && status < 500) return 'border-orange-500 bg-orange-50 dark:bg-orange-900/10';
    if (status >= 500) return 'border-red-500 bg-red-50 dark:bg-red-900/10';
    return 'border-gray-500 bg-gray-50 dark:bg-gray-900/10';
  };

  // Сокращение URL для отображения
  const truncateUrl = (url) => {
    try {
      if (!url) return '';
      
      const urlObj = new URL(url);
      const host = urlObj.hostname;
      const path = urlObj.pathname;
      
      let displayPath = path;
      if (path.length > 15) {
        displayPath = path.substring(0, 12) + '...';
      }
      
      let display = host + displayPath;
      if (urlObj.search) {
        display += '?...';
      }
      
      return display;
    } catch (e) {
      return url;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <ExternalLink className="w-5 h-5 mr-2 text-indigo-500" />
        Схема перенаправлений
      </h3>

      <div className="relative overflow-x-auto py-4">
        <div className="flex items-start">
          {/* Рисуем цепочку редиректов */}
          {redirectChain.map((hop, index) => (
            <React.Fragment key={index}>
              {/* Карточка URL */}
              <div className={`flex-shrink-0 w-48 p-3 border rounded-lg ${getStatusColor(hop.status)}`}>
                <div className="flex items-center mb-2">
                  <div className="mr-2">
                    {getStatusIcon(hop.status)}
                  </div>
                  <div className="text-sm font-medium">
                    {hop.status} {hop.statusText}
                  </div>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 break-all">
                  {truncateUrl(hop.url)}
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                  {hop.timing?.total ? `${hop.timing.total} мс` : ''}
                </div>
              </div>

              {/* Стрелка между URL */}
              {index < redirectChain.length - 1 && (
                <div className="flex-shrink-0 mx-2 flex items-center justify-center h-full">
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Линия соединения */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-300 dark:bg-gray-600 -mt-px z-0"></div>
      </div>

      {/* Информация о редиректах */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 text-sm">
        <p className="text-gray-700 dark:text-gray-300">
          {redirectChain.length === 1 ? (
            "Данный URL не имеет перенаправлений и загружается напрямую."
          ) : (
            `Обнаружена цепочка из ${redirectChain.length - 1} перенаправлений. 
             ${redirectChain.filter(h => h.status === 301).length > 0 ? 'Используются постоянные (301) редиректы.' : 'Используются временные редиректы.'}`
          )}
        </p>
        {redirectChain.length > 2 && (
          <p className="mt-2 text-orange-600 dark:text-orange-400">
            <AlertTriangle className="w-4 h-4 inline-block mr-1" />
            Цепочка редиректов слишком длинная. Рекомендуется сократить количество перенаправлений для улучшения скорости загрузки и SEO.
          </p>
        )}
      </div>
    </div>
  );
};

export default RedirectMap;